import React from 'react'
import { useTranslate } from 'src/localization/Trns'
import { useQuerySpendChart } from 'src/queries/reporting/domain/useQuerySpendChart'

import * as style from '../Reporting.style'
import {
  formatDate,
  hasSpendData,
  ReportingPanelQueryParams,
} from './reportingPanelUtils'
import TopSpendersList from './TopSpendersList'

const TopSpendersLoadingState: React.FC = () => {
  const translate = useTranslate()

  return (
    <div aria-label={translate('Loading top spenders')}>
      {[0, 1, 2, 3, 4].map((row) => (
        <div key={row} css={style.topSpenderSkeletonRow}>
          <span css={style.topSpenderSkeletonAvatar} />
          <span css={style.topSpenderSkeletonName} />
          <span css={style.topSpenderSkeletonAmount} />
        </div>
      ))}
    </div>
  )
}

const MessageState: React.FC<{
  message: string
  detail?: string
  retryLabel?: string
  onRetry?: () => void
}> = ({ message, detail, retryLabel, onRetry }) => (
  <div css={style.panelMessage}>
    <p>{message}</p>
    {detail ? <span css={style.panelMessageDetail}>{detail}</span> : null}
    {retryLabel && onRetry ? (
      <button type="button" css={style.retryButton} onClick={onRetry}>
        {retryLabel}
      </button>
    ) : null}
  </div>
)

const SpendByPersonPanel: React.FC<ReportingPanelQueryParams> = ({
  organizationId,
  currency,
  enabled,
  dateRange,
}) => {
  const translate = useTranslate()
  const query = useQuerySpendChart(
    {
      organizationId,
      chart: 'spend_by_person',
      since: formatDate(dateRange.since),
      until: formatDate(dateRange.until),
      currency,
    },
    enabled,
  )

  return (
    <section
      css={style.chartPanel}
      aria-labelledby="spend_by_person-title"
      data-testid="top-spenders-panel"
    >
      <div css={style.panelHeader}>
        <h2 id="spend_by_person-title" css={style.chartTitle}>
          {translate('Top spenders')}
        </h2>
      </div>
      <div
        css={style.topSpendersBody}
        data-testid="reporting-chart-spend_by_person"
      >
        {query.isLoading ? (
          <TopSpendersLoadingState />
        ) : query.isError ? (
          <MessageState
            message={translate('Unable to load top spenders.')}
            detail={translate('Try again to refresh this panel.')}
            retryLabel={translate('Retry')}
            onRetry={() => query.refetch()}
          />
        ) : query.data?.chart && hasSpendData(query.data.chart) ? (
          <TopSpendersList chart={query.data.chart} currency={currency} />
        ) : (
          <MessageState
            message={translate('No spend in this period.')}
            detail={translate('Try a wider date range.')}
          />
        )}
      </div>
    </section>
  )
}

export default SpendByPersonPanel
